import { useUnresolvedThreads } from 'features/libra/comments/commentsApi'
import { buildDashboardConfig } from 'features/libra/dashboard/ticketDashboard/libra/convert/buildDashboardConfig'
import { toCardViews } from 'features/libra/dashboard/ticketDashboard/libra/convert/toCardViews'
import { indexTickets } from 'features/libra/dashboard/ticketDashboard/libra/data/indexTickets'
import { createSignoffPills } from 'features/libra/dashboard/ticketDashboard/libra/pills/signoffPills'
import { createStagePills } from 'features/libra/dashboard/ticketDashboard/libra/pills/stagePills'
import type { TicketDashboardSettings } from 'features/libra/dashboard/ticketDashboard/libra/settings/dashboardSettings'
import { useTicketsSignoffEntries } from 'features/libra/signoff/signoffsApi'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'
import { type RolodexEntryQueryBody, useRolodexEntries } from 'features/rolodex'
import uniq from 'lodash/uniq'
import { useMemo } from 'react'
import { RolodexEntryType } from 'types'

const NO_IDS: ReadonlyArray<string> = []

/** Unit and site names in one request. Empty id lists are left out so they don't match everything. */
function useNameLookups(unitIds: string[], siteIds: string[]) {
  const queries = useMemo(() => {
    const bodies: RolodexEntryQueryBody[] = []
    if (unitIds.length > 0) {
      bodies.push({ type: RolodexEntryType.UNIT, ids: unitIds })
    }
    if (siteIds.length > 0) {
      bodies.push({ type: RolodexEntryType.SITE, ids: siteIds })
    }
    return bodies.length > 0 ? bodies : undefined
  }, [unitIds, siteIds])
  return useRolodexEntries(queries)
}

export function useLibraTicketDashboard({
  dashboardId,
  settings,
  tickets,
  workflows,
  hasCreateVersionPermission,
}: {
  dashboardId: string
  settings: TicketDashboardSettings
  /** Every version of every ticket to show. */
  tickets: ReadonlyArray<LibraTicket>
  workflows: ReadonlyArray<LibraWorkflow>
  hasCreateVersionPermission: boolean
}) {
  const units = useMemo(() => indexTickets(tickets), [tickets])
  const unitIds = useMemo(() => units.map((unit) => unit.unitId), [units])
  const siteIds = useMemo(
    () =>
      uniq(
        tickets.flatMap(({ tags }) =>
          typeof tags.site === 'string' ? [tags.site] : []
        )
      ),
    [tickets]
  )
  const headlineIds = useMemo(
    () =>
      units.flatMap((unit) =>
        unit.chains.flatMap((chain) => chain.versions[0]?.id ?? [])
      ),
    [units]
  )

  const { builtIns } = settings
  // Not awaited: cards show the unit id until names arrive.
  const { getUnit, getSite } = useNameLookups(unitIds, siteIds)
  const signoffs = useTicketsSignoffEntries(
    builtIns.signoffs.enabled ? headlineIds : NO_IDS
  )
  const threads = useUnresolvedThreads(
    builtIns.comments.enabled ? headlineIds : NO_IDS
  )

  const stagePills = useMemo(() => createStagePills(workflows), [workflows])
  const signoffPills = useMemo(() => createSignoffPills(workflows), [workflows])
  const workflowsById = useMemo(
    () => new Map(workflows.map((workflow) => [workflow.id, workflow])),
    [workflows]
  )
  const config = useMemo(
    () =>
      buildDashboardConfig(dashboardId, settings, { stagePills, signoffPills }),
    [dashboardId, settings, stagePills, signoffPills]
  )
  const cards = useMemo(
    () =>
      toCardViews({
        units,
        settings,
        stagePills,
        signoffPills,
        workflows: workflowsById,
        getUnitName: (id) => getUnit(id)?.data.name,
        getSiteName: (id) => getSite(id)?.data.name,
        signoffsByTicketId: signoffs.isLoading
          ? undefined
          : signoffs.signoffsByTicketId,
        unresolvedThreadsByTicketId: threads.isLoading
          ? undefined
          : threads.threadsByTicketId,
        hasCreateVersionPermission,
      }),
    [
      units,
      settings,
      stagePills,
      signoffPills,
      workflowsById,
      getUnit,
      getSite,
      signoffs,
      threads,
      hasCreateVersionPermission,
    ]
  )

  return { config, cards }
}
